/**
 * menuContent.tsx
 */
import Container from '@components/atoms/container/menuContentContainer';
import Context from '@shared/components/utils/context/menuContext';
import DeviceConfig from '@devices/config';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import Item from '@components/elements/menu/menuItem';
import List from '@components/sections/list/list';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import useUpdateMenu from '@shared/components/utils/hooks/useUpdateMenu';
import type { MenuItem } from '@shared/components/menu/menuData';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setModal } from '@shared/store/slices/modal.slice';
import { setVideoAd } from '@shared/store/slices/ads.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import {
  emptyRoute,
  selectCurrentParams,
  selectRoute,
} from '@shared/store/slices/router.slice';
import {
  selectMenuCollapse,
  selectMenuDisplay,
  selectMenuResetIndexToken,
} from '@shared/store/slices/menu.slice';

const MENU_LOAD_DELAY = 250;
const MENU_NAVIGATION_DELAY = 100;

/**
 * This component displays a list of navigatable menu items.
 * @param {Props} parentProps - The parent props.
 * @returns {JSX.Element} The list of menu items.
 */
export default function MenuContent() {
  const { callbacks, props, items } = useMenuContent();
  return (
    <Container>
      <List {...props.list} {...callbacks.list}>
        {items.map((item, index) => (
          <Item index={index} key={index} item={item} {...callbacks.item} />
        ))}
      </List>
    </Container>
  );
}

/**
 * Hook to manage the menu content component.
 * @returns {Object} The menu content component.
 */
function useMenuContent() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const dispatch = useAppDispatch();
  const items = useUpdateMenu();

  // Context
  const { effectiveIndex, setListIndex } = useContext(Context);

  // Selectors
  const collapse = useAppSelector(selectMenuCollapse);
  const currentParams = useAppSelector(selectCurrentParams);
  const display = useAppSelector(selectMenuDisplay);
  const focus = useAppSelector(selectFocus);
  const route = useAppSelector(selectRoute);
  const resetIndexToken = useAppSelector(selectMenuResetIndexToken);

  // Refs
  const delayRef = useRef<ReturnType<typeof setTimeout>>();
  const displayRef = useRef<boolean>(display);
  const focusedRef = useRef<boolean>(false);
  const itemsRef = useRef<MenuItem[]>([]);
  const pageRef = useRef(route.Page);
  const stackRef = useRef(route.stack);
  displayRef.current = display;
  focusedRef.current = focus === Focus.MENU;
  itemsRef.current = items;
  pageRef.current = route.Page;
  stackRef.current = route.stack;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const loadAd = useCallback((index: number) => {
    if (!itemsRef.current.length) return;
    if (index < 0) return;
    const adSource = itemsRef.current[index].ad();
    dispatch(setVideoAd(adSource));
  }, []);

  const scheduleLoadPage = useCallback((index: number) => {
    if (!itemsRef.current.length) return;
    if (index < 0) return;
    cleanRoute();
    effectiveIndex.current = index;
    delayRef.current = setTimeout(() => {
      itemsRef.current[index].action();
    }, MENU_LOAD_DELAY);
  }, []);

  const cancelPendingLoad = useCallback(() => {
    clearTimeout(delayRef.current);
  }, []);

  const cleanRoute = useCallback(() => {
    if (pageRef.current !== Pages.EMPTY) {
      dispatch(emptyRoute());
    }
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = useCallback((index: number) => {
    if (
      index < 0 ||
      !displayRef.current ||
      !focusedRef.current ||
      !itemsRef.current.length
    ) {
      return;
    }
    cancelPendingLoad();
    scheduleLoadPage(index);
  }, []);

  const onClick = useCallback((index?: number) => {
    if (!displayRef.current) return;
    if (index === undefined) return;
    if (index < 0) return;
    cancelPendingLoad();
    if (index === effectiveIndex.current && stackRef.current.length === 0) {
      if (itemsRef.current[index].modal) {
        itemsRef.current[index].modal();
      }
      return;
    }
    scheduleLoadPage(index);
    loadAd(index);
    dispatch(setFocus(Focus.MENU));
    setListIndex.current(index);
  }, []);

  const onFocus = useCallback((index: number) => {
    if (!displayRef.current) return;
    if (index < 0) return;
    if (effectiveIndex.current !== index) {
      cancelPendingLoad();
      scheduleLoadPage(index);
    }
  }, []);

  const outOfBounds = useCallback(
    (_listFocus: Focus | number | string, key?: string, pressed?: boolean) => {
      if (!displayRef.current || pressed) return;
      switch (key) {
        case 'UP':
          dispatch(setFocus(Focus.SEARCH));
          return;
      }
    },
    []
  );

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  // If deadman is triggered, reset menu index
  useEffect(() => {
    // When menu is not displayed, a blacklisted page has been loaded.
    // Check if deadman is triggered and reset menu index
    if (!display && currentParams?.deadman) {
      effectiveIndex.current = 0;
      setListIndex.current(0);
    }
  }, [display]);

  // Reset menu index when triggered from outside the menu's context
  // (e.g. login/logout, where MenuContext isn't reachable)
  useEffect(() => {
    if (!resetIndexToken) return;
    effectiveIndex.current = 0;
    setListIndex.current(0);
  }, [resetIndexToken]);

  /**
   * If the search page is loaded, set the effective index to -1.
   * If more pages are to be added, set a new blacklist in pageVisibility.ts and add the page to the blacklist.
   * This prevents the menu from not loading the page when focused again.
   */
  useEffect(() => {
    if (route.Page === Pages.SEARCH) {
      effectiveIndex.current = -1;
    }
  }, [route]);

  // Cancel any pending debounced load on unmount
  useEffect(() => {
    return () => clearTimeout(delayRef.current);
  }, []);

  // ------------------------------------------------------------
  // Keyboard hook
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          if (stackRef.current.length > 0) {
            dispatch(popRoute());
            return;
          }
          if (DeviceConfig.capabilities.exitFromMenu) {
            dispatch(setModal(Modals.EXIT_APP));
          }
          break;
        case 'RIGHT':
          loadAd(effectiveIndex.current);
          break;
        case 'SELECT':
          if (stackRef.current.length !== 0) {
            cancelPendingLoad();
            scheduleLoadPage(effectiveIndex.current);
          }
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      items,
      callbacks: {
        list: {
          onChange,
          onFocus,
          outOfBounds,
        },
        item: {
          onClick,
        },
      },
      props: {
        list: {
          direction: Direction.VERTICAL,
          focus: Focus.MENU,
          focused: focus === Focus.MENU,
          keyDownDelay: MENU_NAVIGATION_DELAY,
          initialIndex: Math.max(0, effectiveIndex.current),
          setListIndex,
        },
      },
    };
  }, [collapse, effectiveIndex, items, onChange, onFocus, outOfBounds]);
}
